import { useQueryClient } from "@tanstack/react-query";
import { Button as AntButton, Input, message, Select, Spin } from "antd";
import DataGrid, { Column, ColumnFixing, FilterRow, HeaderFilter, Paging, Scrolling } from "devextreme-react/data-grid";
import DropDownButton from "devextreme-react/drop-down-button";
import TreeList from "devextreme-react/tree-list";
import "devextreme/dist/css/dx.softblue.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { useLocation } from "react-router-dom";
import { Routes } from "../../../../AppRoutes";
import { setRefreshFolder } from "../../../../actions/documentAction";
import { Box } from "../../../../components/box";
import FontFa from "../../../../components/fontFa/fontFa";
import { CreateNotification } from "../../../../components/notifier";
import { SharePointLinkType, SharePointScope } from "../../../../constants/sharePointConstant";
import { checkLoggingStatus, documentStatus, userAction } from "../../../../constants/statusConstants";
import { getDiffDay } from "../../../../helper/dateTimeHelper";
import { checkingAcessToken, openUrl, showDocumentPerrmissionAccessDenied } from "../../../../helper/util";
import { useUser } from "../../../../hook";
import { useSearchParams } from "../../../../hook/use-search-params";
import WarningDeleteModal from "../../../../modals/warning-delete-modal";
import { ISharepointPermissionProcess } from "../../../../models/ISharepoint";
import { DocumentLinkService } from "../../../../services/document/document-link.service";
import { DocumentService } from "../../../../services/document/document.service";
import { FileService } from "../../../../services/file/file.service";
import { SET_UNAUTHORIZED_ERROR } from "../../../../services/main/main.reducer";
import { PermissionService } from "../../../../services/permission/permission.service";
import { TeamsService } from "../../../../services/teams/teams.service";
import { store, useSelector } from "../../../../store/store";
import { useDocumentTags } from "../../../setting/document/document-tags/hooks/useDocumentTags";
import { useDocumentTemplate } from "../../hooks";
import { useDocumentDepartment } from "../../hooks/documents/use-document-department";
import { useFolder2 } from "../../hooks/documents/use-document-folder";
import { useDocumentType } from "../../hooks/documents/use-document-type";
import AddFileModal from "../../modal/add-file-modal";
import AddFolderModal from "../../modal/add-folder-modal";
import { PublishMultipleFileModal } from "../../modal/publish-multiple-file/publish-multiple-file-modal";
import UpdateFolderModal from "../../modal/update-folder-modal";
import { getTitleForDocument } from "../../util";
import { DocumentDataGridItem } from "./document-datagrid-item";
import DocumentListDraggable from "./document-list-draggable";
import { DepartmentList } from "./permission-department/department-list";

import { produce } from "immer";
import { orderBy } from "lodash";
import { getEnv } from "../../../../AppConfigs";
import HiddenSpace from "../../../../components/ui/hidden-space";
import MESSAGES from "../../../../constants/messages";
import PERMISSIONS from "../../../../constants/permissions";
import { useMineInfo } from "../../../../contexts/mine-info-context";
import DocumentOrderProvider from "../../contexts/DocumentOrderContext";
import useDeleteFolder from "../../hooks/documents/use-delete-folder";
import { useUpdateDocument } from "../../hooks/documents/use-update-document";
import styles from "./document-list.module.scss";

const isDocumentExpired = (effectiveTo: string) => {
  const today = new Date();
  return getDiffDay(effectiveTo, today.toDateString()) >= 0;
};

const anyElementMatches = (arr1: string[], arr2: string[]): boolean => {
  return arr1.some((item1) => arr2.includes(item1));
};

export const DocumentList = (props: any) => {
  const {
    statusQuery,
    filterExpired,
    menuTitle,
    listId,
    isShowCheckColumn,
    isShowAction,
    onSelectedDocument,
    isDepartment,
    onSelectedFolder,
  } = props;
  const user = useSelector((state) => state.user);
  const mainState = useSelector((state) => state.main);
  const email = user?.current?.username;
  const dispatch = useDispatch();
  const [openPermissionModal, setOpenPermissionModal] = useState(false);
  const [openAddFolderModal, setOpenAddFolderModal] = useState(false);
  const [openUpdateFolderModal, setOpenUpdateFolderModal] = useState(false);
  const [openAddFileModal, setOpenAddFileModal] = useState(false);
  const [showDeletedConfirm, setShowDeletedConfirm] = useState(false);
  const [currentDocument, setCurrentDocument] = useState<any>();
  const [currentDocumentPermissionProcess, setCurrentDocumentPermissionProcess] =
    useState<ISharepointPermissionProcess>();
  const [isReadOnlyFolder, setIsReadOnlyFolder] = useState(true);
  const [documentData, setDocumentData] = useState<any>([]);
  const filterScopes = statusQuery === documentStatus.publish.value ? [SharePointScope.users] : [SharePointScope.users];
  const filterTypes =
    statusQuery === documentStatus.publish.value
      ? [SharePointLinkType.view, SharePointLinkType.edit, SharePointLinkType.blocksDownload]
      : [SharePointLinkType.view, SharePointLinkType.edit];

  const [deletedItem, setDeletedItem] = useState<any>();
  const [deletedTitle, setDeletedTitle] = useState<any>();
  const [deletedMessge, setDeletedMessage] = useState<any>();
  const [deleteFolderButtonTooltip] = useState<any>();
  // const [documentRequestId, setDocumentRequestId] = useState<any>();
  const history = useHistory();
  const [searchInputValue, setSearchInputValue] = useState("");
  const inputSearchRef = useRef<HTMLInputElement | null>(null);
  const { searchParams, replaceSearchParams } = useSearchParams();
  const isSystem = !!searchParams.get("system");
  const { activeData: activeTemplateData } = useDocumentTemplate();
  const { data: documentType } = useDocumentType();
  const { data: documentDepartment } = useDocumentDepartment();
  const { dataOptions } = useDocumentTags();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const tags = searchParams.get("tags");
  const { pathname } = useLocation();
  const folderId = searchParams.get("folderId");
  const isDeleteFolder = !!searchParams.get("isDeleteFolder");
  const { data: selectedFolderInfo } = useFolder2();
  const queryClient = useQueryClient();
  const { userAdmin } = useUser();
  const [isShowDataGrid, setIsShowDataGrid] = useState(false);
  const SP_PUBLISH_LIST_ID = getEnv("REACT_APP_PUBLIC_FOLDER_AND_FILE_SP_LIST_ID");

  const { checkPermission } = useMineInfo();

  const { mutate: updateDocument, isLoading: isUpdateDocLoading } = useUpdateDocument();
  const { mutateAsync: deleteFolderAsync } = useDeleteFolder();

  const onChangeOrder = (info: { order: string; data: any }) => {
    const { order, data } = info;
    updateDocument(
      { docId: data.listItemId, fields: { docOrder: order } },
      {
        onSuccess() {
          message.success("Thay đổi vị trí thành công");
          setDocumentData(
            produce((draft: any) => {
              const document = draft.find((doc: any) => doc.listItemId === data.listItemId);
              document.docOrder = Number(order);
            }),
          );
        },
        onError() {
          message.error("Thay đổi vị trí thất bại");
        },
      },
    );
  };

  const toggleAddFolder = () => {
    if (!checkPermission(PERMISSIONS.CREATE_DOC_FOLDER)) return message.error(MESSAGES.NOT_PERMISSION);

    setOpenAddFolderModal(!openAddFolderModal);
    searchParams.delete("addRoot");
    replaceSearchParams(searchParams);
  };

  const toggleAddParentFolder = () => {
    if (!checkPermission(PERMISSIONS.CREATE_DOC_FOLDER)) return message.error(MESSAGES.NOT_PERMISSION);

    setOpenAddFolderModal(!openAddFolderModal);
    searchParams.set("addRoot", "true");
    replaceSearchParams(searchParams);
  };

  const toggleRenameFolder = () => {
    setOpenUpdateFolderModal(!openUpdateFolderModal);
  };

  const goToTeams = async (item: any) => {
    try {
      let itemId = item?.listItemId;
      if (statusQuery === documentStatus.publish.value) itemId = item?.docWorkingId;

      var document = await DocumentService.getDocumentInfo(itemId);
      if (document && document.chatGroupId && document.chatGroupName) {
        var url = TeamsService.getRedirectLinkFromFileChatGroup(document.chatGroupId, document.chatGroupName);
        window.open(url, "_blank");
      }
    } catch (error: any) {
      CreateNotification({ title: "Không thể mở teams", body: error.message, icon: "/icons/error.png" });
      console.log(error);
    }
  };

  const handleDeleteFolderConfirmDialog = async (folderId: string, folderName: string) => {
    if (!checkPermission(PERMISSIONS.DELETE_FOLDER)) return message.error(MESSAGES.NOT_PERMISSION);

    if ((await checkWritePermissionOnDocument(folderId)) === false) return;
    setDeletedTitle("Xoá thư mục");
    setDeletedMessage(
      `Anh/Chị có muốn xóa thư mục không ? Chú ý: những thư mục và tài liệu bên trong thư mục này cũng bị xóa.`,
    );
    setDeletedItem({ id: folderId, name: folderName, isFolder: true });
    setShowDeletedConfirm(true);
  };

  const handleDeleteConfirmDialog = async (item: any) => {
    if (!checkPermission(PERMISSIONS.DELETE_OR_ARCHIVE_DOCUMENT)) return message.error(MESSAGES.NOT_PERMISSION);

    if ((await checkWritePermissionOnDocument(item.id)) === false) return;

    setDeletedTitle("Xoá tài liệu");
    setDeletedMessage(`Bạn có muốn xóa tài liệu '${item?.Title || item?.nameWithoutExt}' không ?`);

    setDeletedItem(item);
    setShowDeletedConfirm(true);
  };

  const handleCloseDeletedConfirm = () => {
    setShowDeletedConfirm(!showDeletedConfirm);
  };

  const handleDeletedConfirm = () => {
    if (deletedItem) processDeletedItem(deletedItem);
  };

  const processDeletedItem = (item: any) => {
    FileService.deletedFile(item.id, listId).then(
      async () => {
        const folderId = searchParams.get("folderId");
        //! [x] shiro - delete-folder  isPublicFolder = false
        if (folderId) await deleteFolderAsync({ key: folderId! });

        const body = "Xoá thành công";
        queryClient.invalidateQueries({ queryKey: ["folders"] });
        if (item.isFolder) {
          searchParams.delete("folderId");
          replaceSearchParams(searchParams);
          try {
            const publishFolder = await FileService.getFolderByPath(item.name);
            if (publishFolder) {
              const file = await FileService.getFile(publishFolder.id, SP_PUBLISH_LIST_ID);
              const id = file.id;
              if (id) {
                await FileService.deletedFile(id, SP_PUBLISH_LIST_ID);
              }
            }
          } catch (error) {
            console.log(error);
          }

          await queryClient.invalidateQueries({ queryKey: ["publish-folders-new"], type: "all" });
        } else {
          getDocumentData();
        }
        CreateNotification({ title: "Thành công", body: body, icon: "/icons/success.png" });
      },
      (error) => {
        CreateNotification({ title: "Thất bại", body: `Xoá thất bại`, icon: "/icons/error.png" });
        console.log(error);
      },
    );
  };

  const openDocumentUrl = (item: any) => {
    if (item?.IsCustomEditor) {
      goToDetail(item);
      return;
    }

    openUrl(item?.url);
  };

  const goToDetail = (item: any, action: string = "", mode: string = "", templateId: any = null) => {
    let filters = [];

    let itemId = item?.listItemId;

    if (statusQuery === documentStatus.publish.value || pathname === "/version-document-content") {
      itemId = item?.docWorkingId;
      filters.push(`action=info`);
    }

    if (!itemId) {
      itemId = 0;
    }

    if (action !== null && action !== "") filters.push(`action=${action}`);

    if (statusQuery === documentStatus.publish.value) {
      filters.push(`status=${statusQuery}`);
    }

    if (templateId) {
      filters.push(`templateId=${templateId}`);
    }

    if (mode === userAction.new && folderId) {
      filters.push(`folderId=${folderId}`);
    }

    if (mode !== "") {
      filters.push(`mode=${mode}`);
    }

    if (item?.id) {
      filters.push(`fileId=${item.id}`);
    }

    if (item?.docTypeId !== "1") {
      filters.push(`action=info`);
    }

    if (statusQuery === documentStatus.needProcess.value) {
      filters.push(`action=link`);
    }

    let filterQuery = "";
    if (filters.length > 0) {
      filterQuery = `?${filters.join("&")}`;
    }
    history.push(Routes.documentDetail.path.replace(":id", `${itemId}${filterQuery}`));
  };

  const goToDetailProcess = (item: any) => {
    goToDetail(item, userAction.process);
  };

  const goToDetailEdit = async (item: any) => {
    if (!(await checkWritePermissionOnDocument(item))) return;

    goToDetail(item, "", userAction.edit);
  };

  const goToPermission = async (item: any) => {
    if (!(await checkWritePermissionOnDocument(item))) return;
    if (item.docStatusValue === "publish") {
      CreateNotification({
        title: "Thông báo",
        body: `Tài liệu đã ban hành, vui lòng chuyển qua tab "Tài liệu ban hành" để phân phối bổ sung`,
        icon: "/icons/warning.png",
      });
      return;
    }

    try {
      var result = await PermissionService.getDocumentPermissons(item.id, listId, item.isFolder);
      if (!(await checkWritePermissionOnDocument(item, result.data))) return;
      if (result.count > 0) {
        setCurrentDocumentPermissionProcess(result.data);
      }
      setCurrentDocument(item);
      setOpenPermissionModal(!openPermissionModal);
    } catch (error: any) {
      CreateNotification({ title: "Không thể truy cập phân quyền", body: error.message, icon: "/icons/error.png" });
      console.log(error);
    }
  };

  const checkWritePermissionOnDocument = async (id: string, currentDocumentPermissionProcess: any[] = []) => {
    try {
      var hasWritePermission = await PermissionService.checkWritePermission(
        user,
        id,
        listId,
        isDeleteFolder,
        userAdmin?.id,
      );
      if (currentDocumentPermissionProcess && currentDocumentPermissionProcess?.length > 0) {
        const userEmail = user?.current?.username?.toLowerCase();
        if (
          currentDocumentPermissionProcess.findIndex((e) => e.users[0].email === userEmail && e.roles[0] === "write") >
          -1
        )
          hasWritePermission = true;
      }
      if (!hasWritePermission) {
        CreateNotification({
          title: "Không thể chỉnh sửa",
          body: `Bạn không có quyền chỉnh sửa file này`,
          icon: "/icons/error.png",
        });
        return false;
      }
    } catch (error: any) {
      CreateNotification({ title: "Không thể truy cập", body: error.message, icon: "/icons/error.png" });
      console.log(error);
      return false;
    }

    return true;
  };

  const toggleAddFile = () => {
    if (!checkPermission(PERMISSIONS.UPLOAD_DOCUMENT)) return message.error(MESSAGES.NOT_PERMISSION);

    setOpenAddFileModal(!openAddFileModal);
  };

  const checkPermissionOnSelectedFolder = useCallback(() => {
    try {
      PermissionService.checkWritePermission(user, folderId, listId, true, userAdmin?.id).then(
        (res) => {
          setIsReadOnlyFolder(!res);
        },
        (error) => {
          console.log(error);
        },
      );
    } catch (error) {
      console.log(error);
    }
  }, [folderId, listId, user, userAdmin?.id]);

  const getDocuments = useCallback(async () => {
    try {
      checkPermissionOnSelectedFolder();
      const paramListId = listId;
      let results: any = [];
      if (folderId) {
        results = await FileService.getChild(folderId, paramListId, true);
      } else {
        results = await FileService.getRootFolder(paramListId, true);
      }

      const docTags = tags?.split(",");
      if (!docTags) return results;
      results.data = results?.data?.filter((e: any) => e.Tags?.length > 0 && anyElementMatches(e.Tags, docTags));
      return results;
    } catch (error: any) {
      if (error.status === 403) {
        showDocumentPerrmissionAccessDenied();
      } else if (error.status === 401) {
        store.dispatch({
          type: SET_UNAUTHORIZED_ERROR,
          status: true,
        });
      }
      setDocumentData([]);
      return { data: [], count: 0 };
    }
  }, [checkPermissionOnSelectedFolder, folderId, listId, tags]);

  const getDocumentLinkExpired = useCallback(
    async (listItemId: string) => {
      if (statusQuery || !folderId || pathname.includes("statistic")) return false;
      const res = await DocumentLinkService.getDocumentLinkInfos(listItemId);
      const dataArr = !res?.data ? [] : [...res.data];
      const isHasExpired = dataArr.findIndex((e) => isDocumentExpired(e.docLinkExpiredDate) && !e.isDeleted) > -1;
      return isHasExpired;
    },
    [folderId, pathname, statusQuery],
  );

  const getDocumentData = useCallback(() => {
    getDocuments().then((results) => {
      if (!results) return;
      results.data?.forEach((obj1: any) => {
        const docTypeObject = documentType?.find((obj2: any) => obj2.value === obj1.docTypeId);
        const docPublicDepartmentObject = documentDepartment?.find(
          (obj2: any) => obj2.value === obj1.docPublicDepartmentLookupId,
        );
        if (docTypeObject) obj1.docType = docTypeObject.label;
        if (docPublicDepartmentObject) obj1.docPublicDepartment = docPublicDepartmentObject.label;
      });

      if (!statusQuery) {
        const promises = results.data.map((item: any) => {
          return getDocumentLinkExpired(item.listItemId).then((isExpired) => ({ item, isExpired }));
        });

        Promise.all(promises)
          .then((items) => {
            // Now that all promises have resolved, we have an array of objects containing each item and its expired status
            // Sort the array based on the expired status
            items.sort((a, b) => {
              if (a.isExpired && !b.isExpired) {
                return -1;
              } else if (!a.isExpired && b.isExpired) {
                return 1;
              } else {
                return 0;
              }
            });

            // Finally, map the sorted items back to their original format (excluding the expired status)
            const sortedArr = items.map((item) => item.item);
            setDocumentData(sortedArr);
            // Now sortedArr contains the sorted array
          })
          .catch((error) => {
            // Handle any errors
            console.error(error);
          });
      } else {
        setDocumentData(results.data);
      }

      if (isDepartment) onSelectedFolder(results.data);
    });
  }, [
    documentType,
    getDocumentLinkExpired,
    getDocuments,
    isDepartment,
    onSelectedFolder,
    documentDepartment,
    statusQuery,
  ]);

  const onImportFileCompleted = (files: any[]) => {
    if (files.length > 1) dispatch(setRefreshFolder(true));
    files.forEach((file) => {
      const url = Routes.documentDetail.path.replace(
        ":id",
        `${file.docId}?&action=${userAction.info}&mode=${userAction.edit}&fileId=${file.fileId}`,
      );
      window.open(url, "_blank"); // Open each file in a new tab
    });
    getDocumentData();
  };

  const handleHidePermissionModal = () => {
    setOpenPermissionModal(!openPermissionModal);
  };

  const showFolder = () => {
    return !statusQuery || (statusQuery === documentStatus.publish.value && ["false", ""].includes(filterExpired));
  };

  const validFile = (file: any) => {
    return documentData.find((cf: any) => cf.name === file.name) != null ? MESSAGES.DUPLICATE_DOCUMENT : "";
  };

  const createDocumentButtonRender = () => {
    return (
      activeTemplateData?.length > 0 && (
        <TreeList
          width="400px"
          id="content"
          dataSource={activeTemplateData}
          autoExpandAll={true}
          showRowLines={true}
          showBorders={true}
          columnAutoWidth={true}
          onRowClick={onDocumentTemplateClick}
          keyExpr="Id"
          showColumnHeaders={false}
          parentIdExpr="CategoryLookupId"
        >
          <Scrolling mode="standard" />
          {/* <SearchPanel visible={true} width={240} placeholder="Search..." />  */}
          <Column dataField="Title" dataType="string" />
        </TreeList>
      )
    );
  };

  const onDocumentTemplateClick = (e: any) => {
    if (!e.data.CategoryLookupId) return;

    goToDetail(null, "", userAction.new, e.data.Id);
  };

  const onNewDocumentClick = () => {
    if (!checkPermission(PERMISSIONS.CREATE_DOC_FOLDER)) return message.error(MESSAGES.NOT_PERMISSION);

    goToDetail(null, "info", userAction.new);
  };

  useEffect(() => {
    if (checkingAcessToken(user, mainState) !== checkLoggingStatus.OK) {
      return;
    }

    getDocumentData();

    if (inputSearchRef.current) {
      inputSearchRef.current.value = "";
    }
  }, [user, mainState, getDocumentData]);

  const handleSearchInputChange = async (e: any) => {
    const searchInputValue = e;
    setSearchInputValue(searchInputValue);
    try {
      if (searchInputValue) {
        const res = await DocumentService.searchDocuments(searchInputValue);
        if (res.length === 0) setDocumentData([]);
        const result = await DocumentService.getDocumentByListId(res);
        if (result.count !== 0) {
          setIsShowDataGrid(true);
          setDocumentData(result.data);
        }
      } else {
        if (folderId) {
          getDocumentData();
        } else {
          setIsShowDataGrid(false);
        }
      }
    } catch (error) {
      // Handle the error here
      console.error("An error occurred:", error);
    }
  };

  const contentGridTitle = searchInputValue
    ? `Danh sách tìm kiếm: ${searchInputValue}`
    : selectedFolderInfo?.data?.name || menuTitle;

  const handleFilterClick = () => {
    if (selectedTags.length === 0) {
      searchParams.delete("tags");
      replaceSearchParams(searchParams);
      return;
    }
    searchParams.set("tags", selectedTags.toString());
    replaceSearchParams(searchParams);
  };

  const sortedDocumentData = useMemo(() => {
    return orderBy(
      documentData,
      [
        (item) => (item?.docOrder === undefined ? Infinity : item.docOrder),
        (item) => new Date(item.LastModifiedDateTime).getTime(),
      ],
      ["asc", "desc"],
    );
  }, [documentData]);

  const loading = isUpdateDocLoading;

  return (
    <>
      <div className={styles.container}>
        <div className={styles.sidebar}>
          <HiddenSpace hidden={!showFolder()}>
            <Box
              title="Danh sách thư mục"
              titleRight={
                <Button
                  title="Tạo thư mục"
                  onClick={toggleAddParentFolder}
                  className="mx-2 mt-2"
                  variant="outline-warning"
                  hidden={isShowCheckColumn || statusQuery || mainState.isForbidden}
                >
                  <FontFa name="faFolderPlus" size={"lg"} />
                </Button>
              }
              className="menu-item mb-2"
            >
              <Input.Search
                placeholder="Nhập tên tài liệu cần tìm"
                onSearch={handleSearchInputChange}
                enterButton
                allowClear
              />
              <div className={styles.documentList}>
                <DocumentListDraggable />
              </div>
            </Box>
          </HiddenSpace>
        </div>
        <div className={styles.content}>
          {folderId || !showFolder() || isShowDataGrid ? (
            <Col sm={8} md={9}>
              <div className={"Content__grid-hide"}>
                <Box
                  title={<span>{contentGridTitle}</span>}
                  titleRight={
                    !statusQuery && (
                      <div className="px-3 py-2 d-flex align-items-center">
                        {!isReadOnlyFolder && isShowAction && (
                          <>
                            <DropDownButton
                              text="Tạo tài liệu mới"
                              className="mr-2"
                              stylingMode="outlined"
                              icon="plus"
                              width={300}
                              splitButton={true}
                              onButtonClick={onNewDocumentClick}
                              dropDownContentRender={createDocumentButtonRender}
                            />
                            <PublishMultipleFileModal
                              documentData={
                                !sortedDocumentData
                                  ? []
                                  : sortedDocumentData.filter(
                                      (record: any) =>
                                        record.docStatusValue !== documentStatus.publish.value &&
                                        record.type !== "html",
                                    )
                              }
                              getDocumentData={getDocumentData}
                              disabled={mainState.isForbidden}
                            />
                            <Button
                              disabled={mainState.isForbidden}
                              title={"Tạo thư mục con"}
                              onClick={toggleAddFolder}
                              className="mr-2"
                              variant="outline-warning"
                            >
                              <FontFa name="faFolderPlus" size={"lg"} />
                            </Button>
                            {folderId && (
                              <Button
                                disabled={mainState.isForbidden}
                                title={"Đổi tên thư mục"}
                                onClick={toggleRenameFolder}
                                className="mr-2"
                                variant="outline-warning"
                              >
                                <FontFa name="faFileMedicalAlt" size={"lg"} />
                              </Button>
                            )}
                            <Button
                              disabled={mainState.isForbidden}
                              title={"Tải tài liệu"}
                              onClick={toggleAddFile}
                              className="mr-2"
                              variant="outline-info"
                            >
                              <FontFa name="faUpload" size={"lg"} />
                            </Button>
                            <Button
                              disabled={mainState.isForbidden}
                              title={"Phân quyên cho thư mục"}
                              className="mr-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                goToPermission({ id: folderId, Title: selectedFolderInfo?.data?.name, isFolder: true });
                              }}
                              variant="outline-primary"
                            >
                              <FontFa name="faLock" size={"sm"} />
                            </Button>

                            <Button
                              disabled={mainState.isForbidden || !folderId}
                              title={deleteFolderButtonTooltip}
                              onClick={(e) => {
                                e.stopPropagation();
                                searchParams.set("isDeleteFolder", "true");
                                replaceSearchParams(searchParams);
                                handleDeleteFolderConfirmDialog(
                                  folderId || "root",
                                  selectedFolderInfo?.data?.name || "",
                                );
                              }}
                              className="mr-2"
                              variant="outline-warning"
                            >
                              <FontFa name="faTrash" size={"sm"} />
                            </Button>
                          </>
                        )}
                      </div>
                    )
                  }
                >
                  <div className="d-flex justify-content-between">
                    <div className="d-flex column">
                      <Select
                        options={dataOptions}
                        placeholder="Vui lòng chọn chủ đề"
                        mode="multiple"
                        size="middle"
                        optionFilterProp="label"
                        value={selectedTags}
                        style={{ width: "400px" }}
                        onChange={(value) => setSelectedTags(value)}
                        allowClear
                      />
                      <AntButton
                        style={{ paddingTop: "-20px", paddingBottom: "-10px", marginLeft: "10px" }}
                        onClick={() => handleFilterClick()}
                        type="primary"
                        size="middle"
                      >
                        Lọc dữ liệu
                      </AntButton>
                    </div>
                    <p className="font-weight-bold align-items-baseline">
                      Tổng cộng:{" "}
                      <span className="px-1 text-primary" style={{ fontSize: "22px" }}>
                        {sortedDocumentData?.length}
                      </span>{" "}
                      tài liệu
                    </p>
                  </div>
                  <div className="w-full overflow-auto">
                    <DocumentOrderProvider onChangeOrder={onChangeOrder}>
                      <Spin spinning={loading}>
                        <DataGrid
                          id="grid__title-container"
                          dataSource={sortedDocumentData}
                          noDataText="Không tìm thấy tài liệu"
                          showBorders={true}
                          disabled={isDepartment}
                          showColumnLines={true}
                          rowAlternationEnabled={true}
                          wordWrapEnabled={true}
                          width={2000}
                          pager={{ displayMode: "adaptive" }}
                          dataRowRender={(datRow) => (
                            <DocumentDataGridItem
                              document={datRow.data}
                              statusView={statusQuery}
                              isShowAction={isShowAction}
                              isShowCheckColumn={isShowCheckColumn}
                              openDocumentUrl={openDocumentUrl}
                              goToDetail={goToDetail}
                              goToDetailEdit={goToDetailEdit}
                              searchInputValue={searchInputValue}
                              goToPermission={goToPermission}
                              goToDetailProcess={goToDetailProcess}
                              goToTeams={goToTeams}
                              listId={listId}
                              deleteConfirmDialog={handleDeleteConfirmDialog}
                              onSelectedDocument={onSelectedDocument}
                              wordWrapEnabled={true}
                              filterExpired={filterExpired}
                              currentEmail={email}
                              getDocumentLinkExpired={getDocumentLinkExpired}
                            />
                          )}
                        >
                          <Scrolling mode="standard" />
                          <ColumnFixing enabled={true} />
                          <FilterRow visible={true} applyFilter={true} />
                          <HeaderFilter visible={true} />
                          <Paging defaultPageSize={10} />
                          {isShowCheckColumn && <Column width={40} fixed={true} fixedPosition="left" />}
                          {!isShowCheckColumn && <Column name="actions" fixed={true} width={150} alignment="center" />}
                          <Column
                            fixed={true}
                            fixedPosition="left"
                            dataField="Title"
                            caption="Tên tài liệu"
                            minWidth={400}
                            allowFixing={true}
                            allowHeaderFiltering={false}
                          />
                          <Column
                            dataField="docCode"
                            caption="Mã tài liệu"
                            allowHeaderFiltering={false}
                            alignment="left"
                          />
                          <Column dataField="docType" caption="Loại tài liệu" />
                          <Column dataField="docStatus" caption="Tình trạng" />
                          <Column
                            dataField="createdDateTime"
                            dataType="date"
                            selectedFilterOperation="between"
                            caption="Ngày hiệu lực"
                            width={150}
                          />
                          <Column
                            dataField="docPublishViewVersion"
                            caption="Lần ban hành"
                            dataType="number"
                            allowHeaderFiltering={false}
                            width={120}
                          />
                          <Column
                            dataField="docEditionVersion"
                            caption="Lần sửa đổi"
                            dataType="number"
                            allowHeaderFiltering={false}
                            width={100}
                          />
                          <Column
                            dataField="createdDateTime"
                            caption="Thời gian khởi tạo"
                            dataType="date"
                            selectedFilterOperation="between"
                            allowHeaderFiltering={false}
                          />
                          <Column dataField="createdUser.displayName" caption="Người khởi tạo" dataType="string" />
                          <Column
                            dataField="lastModifiedDateTime"
                            caption="Thời gian cập nhật"
                            dataType="date"
                            selectedFilterOperation="between"
                            allowHeaderFiltering={false}
                          />
                          <Column dataField="modifiedUser.displayName" caption="Người cập nhật" dataType="string" />
                          <Column dataField="tags" caption="Chủ đề" allowHeaderFiltering={false} width={200}>
                            <HeaderFilter />
                          </Column>
                          <Column caption="Vị trí" dataField="id" dataType="string" width={100} />
                        </DataGrid>
                      </Spin>
                    </DocumentOrderProvider>
                  </div>
                </Box>
              </div>
            </Col>
          ) : isSystem ? (
            <Col sm={8} md={9}>
              <img src="assets/images/chinhsachATCL.jpg" alt="" height="60%" />
            </Col>
          ) : (
            <Col sm={{ span: 5, offset: 3 }} md={{ span: 4, offset: 3 }}>
              <img src="assets/images/quytrinhtieuchuan.jpg" alt="" />
            </Col>
          )}
        </div>
      </div>
      <DepartmentList
        id={currentDocument?.id}
        title={getTitleForDocument(currentDocument)}
        listId={listId}
        isDocumentLibrary={true}
        linkProcess={currentDocumentPermissionProcess}
        filterScopes={filterScopes}
        filterTypes={filterTypes}
        isOpen={openPermissionModal}
        onClose={handleHidePermissionModal}
        document={currentDocument}
        isPublisStatus={statusQuery}
      />
      <AddFolderModal onClose={toggleAddFolder} isOpen={openAddFolderModal} />
      <UpdateFolderModal
        onClose={toggleRenameFolder}
        isOpen={openUpdateFolderModal}
        oldFolderName={selectedFolderInfo?.data?.name || ""}
        folderId={selectedFolderInfo?.data?.listItem?.id}
      />
      <AddFileModal
        onClose={toggleAddFile}
        onAddFileCompleted={onImportFileCompleted}
        isOpen={openAddFileModal}
        validFile={validFile}
      />
      <WarningDeleteModal
        data={deletedItem}
        title={deletedTitle}
        message={deletedMessge}
        isOpen={showDeletedConfirm}
        onDelete={handleDeletedConfirm}
        onClose={handleCloseDeletedConfirm}
      />
    </>
  );
};
